import { Picture } from '@ui';
import type { ProductBuyBtnProps } from './components/ProductBuyBtn';
import ProductBuyBtn from './components/ProductBuyBtn';
import BannersLink from '@components/BannersLink';
import LoadingSkeleton from '@components/LoadingSkeleton';

import ProductImgWrapper from './components/ProductImgWrapper';
import measures from '@utils/measures';
import WeightTabs from './componentsNew/WeightTabs';
import icons from '@icons';
import InStoreBlock from './components/ProductImgWrapper/components/InStoreBlock';
import { observer } from 'mobx-react';
import { twcls } from '@utils/twcls';
import { forwardRef, type ReactNode, useMemo } from 'react';
import clsx from 'clsx';
import './ProductCardNew.scss';
import type { CatalogItemCardFragmentFragment } from '@schema/cart';
import { getProductCanBeBought } from '@utils/getProductCanBeBought';

const {
  star: { yellow: starFilledYellow, grey: starFilledGrey }
} = icons.Fill;

const BIG_MOBILE_CARD__CLS = 'allMd:!h-[363px] allMd:pt-[11px]';

const BIG_MOBILE_PRICE__CLS = 'allMd:!text-[20px]';

const BIG_MOBILE_PRICE_PART__CLS = 'allMd:!text-[14px]';

interface ProductCardNewProps {
  is_not_for_sale?: string | null;
  isMenu: boolean;
  is_18plus: boolean;
  isBarMenuCurrentPath: boolean;
  multiPricePromo;
  available;
  badges;
  badgesBottom;
  changePostponedStatus;
  comments_number;
  currentPriceField;
  deliveryDate;
  gds_count;
  id;
  link: string;
  imgsArr;
  isExpressPickupAval;
  isFavorite;
  isFreeze;
  isExpressDeliveryAval;
  isPostponed;
  isShowLongBuyBtn;
  isShowPostponed;
  isThanksForPurchasePage;
  isVesovoi;
  measureItem;
  oldPriceField;
  omImgHoverEnd;
  onAddToOrderClickHandler;
  onChangeWeightTabHandler;
  onImgHover;
  prepareToAddToFavourites;
  priceBlockWeightPrice;
  priceMeasure;
  productCount;
  productInfo: CatalogItemCardFragmentFragment;
  productOfDay;
  rating_comments;
  showCompleteModal;
  specialPriceData;
  symbolPriceField;
  title;
  totalWeight;
  variants;
  weight_of_goods;
  isDesktop;
  isLeaveCommentBtn;
  leaveReviewHandler;
  isPreOrder;
  supplyDate;
  isProductInCart;
  isDigital;
  openInNewPage;
  task;
  onAddToOrderFirst;
  isConsentModalAgreed: boolean;
  isBigMobileCard?: boolean;
  isStatic?: boolean;
}

export const ProductCardLoading = () => (
  <div className={'banners-link product-card-new'}>
    <div className='relative'>
      <div className='ProductImgWrapper'>
        <div className='ProductImgWrapper__track'>
          <LoadingSkeleton className='ProductImgWrapper__img-wrapper !rounded-[12px]' />
        </div>
      </div>
      <div className='ProductImgWrapper__imgs-controllers' />
    </div>
    <div className='product-card-new__info'>
      <div className='product-card-new__rating-vendor'>
        <LoadingSkeleton className='product-card-new__rating-wrapper w-1/3' />
        <LoadingSkeleton className='product-card-new__vendor-code w-2/5' />
      </div>
      <div className='product-card-new__title'>
        <LoadingSkeleton className='w-full h-[1lh]' />
        <LoadingSkeleton className='w-2/3 h-[1lh]' />
      </div>
      <div className='product-card-new__badges-bottom' />
      <div className='product-card-new__bottom'>
        <LoadingSkeleton className='product-card-new__measure w-1/3' />
        <div className='product-card-new__price-btn'>
          <div className='product-card-new__price-wrapper'>
            <LoadingSkeleton className='product-card-new__price w-[80px]' />
          </div>
          <div className='ProductBuyBtns'>
            <LoadingSkeleton className='ProductBuyBtns__plus [&::before]:hidden [&::after]:hidden' />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ProductCardNew = observer(
  forwardRef<HTMLAnchorElement, ProductCardNewProps>(
    (
      {
        is_18plus,
        isConsentModalAgreed,
        isMenu,
        multiPricePromo,
        available,
        badges,
        badgesBottom,
        changePostponedStatus,
        comments_number,
        currentPriceField,
        deliveryDate,
        gds_count,
        id,
        isBarMenuCurrentPath,
        link,
        imgsArr,
        isExpressPickupAval,
        isFavorite,
        isFreeze,
        isExpressDeliveryAval,
        isPostponed,
        isShowLongBuyBtn,
        isShowPostponed,
        isThanksForPurchasePage,
        isVesovoi,
        measureItem,
        oldPriceField,
        omImgHoverEnd,
        onAddToOrderClickHandler,
        onChangeWeightTabHandler,
        onImgHover,
        prepareToAddToFavourites,
        priceBlockWeightPrice,
        priceMeasure,
        productCount,
        productInfo,
        productOfDay,
        rating_comments,
        showCompleteModal,
        specialPriceData,
        symbolPriceField,
        title,
        totalWeight,
        variants,
        weight_of_goods,
        isDesktop,
        isLeaveCommentBtn,
        leaveReviewHandler,
        isPreOrder,
        supplyDate,
        isProductInCart,
        isDigital,
        openInNewPage,
        task,
        onAddToOrderFirst,
        is_not_for_sale,
        isBigMobileCard,
        isStatic
      },
      ref
    ) => {
      const MobileComp = useMemo<ReactNode>(() => {
        if (
          (isMenu && isBarMenuCurrentPath) ||
          (!isMenu && !isBarMenuCurrentPath)
        ) {
          return (
            <div className='product-card-new__mobile-price'>
              <div
                className={clsx(
                  'product-card-new__old-price',
                  productCount > 0 && ' product-card-new__old-price--centered'
                )}
              >
                {isProductInCart
                  ? isVesovoi && !isDesktop
                    ? totalWeight
                    : `${productCount} шт`
                  : `${oldPriceField?.wholePart || 0} ₽`}
              </div>

              <div
                className={clsx(
                  'product-card-new__price',
                  oldPriceField?.wholePart > 0 &&
                    !specialPriceData &&
                    ' product-card-new__price--red'
                )}
              >
                {currentPriceField.wholePart}
                {currentPriceField.floatPart && (
                  <span className='product-card-new__price__decimal'>
                    ,{currentPriceField.floatPart}
                  </span>
                )}
                <span className='product-card-new__price-ruble'>
                  <span>&nbsp;₽</span>
                  {priceMeasure}
                </span>
              </div>
            </div>
          );
        }
        return null;
      }, [
        currentPriceField.floatPart,
        currentPriceField.wholePart,
        isBarMenuCurrentPath,
        isDesktop,
        isMenu,
        isProductInCart,
        isVesovoi,
        oldPriceField?.wholePart,
        priceMeasure,
        productCount,
        specialPriceData,
        totalWeight
      ]);

      const buyBtnProps = useMemo<
        Omit<ProductBuyBtnProps, 'cartStore' | 'modalsStore'>
      >(
        () => ({
          isHideSubscribeBtn: isMenu || (is_18plus && !isConsentModalAgreed),
          productId: id,
          isShowLongBuyBtn,
          isDesktop,
          MobileComp: isMenu || available ? MobileComp : null,
          available,
          showCompleteModal,
          isPreOrder,
          isDigital,
          onAddToOrderFirst,
          ...(isThanksForPurchasePage
            ? {
                mutateOrder: onAddToOrderClickHandler,
                mutateOrderQuantity: productCount
              }
            : {})
        }),
        [
          MobileComp,
          available,
          id,
          isConsentModalAgreed,
          isDesktop,
          isDigital,
          isMenu,
          isPreOrder,
          isShowLongBuyBtn,
          isThanksForPurchasePage,
          is_18plus,
          onAddToOrderClickHandler,
          onAddToOrderFirst,
          productCount,
          showCompleteModal
        ]
      );

      const InStoreBlockComp = isProductInCart ? (
        <InStoreBlock
          specialPriceData={specialPriceData}
          isDesktop={isDesktop}
          isBarMenuCurrentPath={isBarMenuCurrentPath}
          productCount={isDesktop && isVesovoi ? totalWeight : productCount}
          deliveryDate={deliveryDate}
          multiPricePromo={multiPricePromo}
          countInStore={gds_count}
        />
      ) : null;

      if (!productInfo) {
        return null;
      }
      const cardContent = (
        <>
          <ProductImgWrapper
            is_18plus={is_18plus}
            isConsentModalAgreed={isConsentModalAgreed}
            isBarMenuCurrentPath={isBarMenuCurrentPath}
            badges={badges}
            isFavorite={isFavorite}
            prepareToAddToFavourites={prepareToAddToFavourites}
            isShowPostponed={isShowPostponed}
            isPostponed={isPostponed}
            changePostponedStatus={changePostponedStatus}
            isFreeze={isFreeze}
            imgsArr={imgsArr}
            isDesktop={isDesktop}
            title={title}
            productOfDay={productOfDay}
            isExpressPickupAval={isExpressPickupAval}
            onImgHover={onImgHover}
            omImgHoverEnd={omImgHoverEnd}
            productCount={productCount}
            InStoreBlockComp={InStoreBlockComp}
            isExpressDeliveryAval={isExpressDeliveryAval}
            task={task}
            isBigMobileCard={isBigMobileCard}
            isStatic={isStatic}
          />
          <div className='product-card-new__info'>
            <div className='product-card-new__rating-vendor'>
              <div className='product-card-new__rating-wrapper'>
                <Picture
                  icon={comments_number > 0 ? starFilledYellow : starFilledGrey}
                  height={isDesktop ? '20' : '12'}
                  className='product-card-new__star'
                />
                {
                  comments_number > 0 && (
                    <>
                      <div className='product-card-new__rating-count'>
                        {rating_comments}
                      </div>
                      {comments_number > 0 && (
                        <div className='product-card-new__comments-number'>{`(${comments_number})`}</div>
                      )}
                    </>
                  )
                  // : (
                  //   <div className='product-card-new__rating-count--grey'>
                  //     Не оценен
                  //   </div>
                  // )
                }
              </div>
              <div className='product-card-new__vendor-code'>
                <span className='allMd:hidden'>арт. </span>
                {id}
              </div>
            </div>
            {title && <div className='product-card-new__title'>{title}</div>}
            {!available && supplyDate ? (
              <div className='text-sm allMd:text-xs font-medium text-[var(--dark-red)] mb-1 allMd:mb-0 '>
                Ожидается {supplyDate}
              </div>
            ) : badgesBottom?.length > 0 ? (
              <div className='product-card-new__badges-bottom'>
                {badgesBottom.map(item => (
                  <div
                    key={item.id}
                    className={'product-card-new__badge-bottom'}
                    id={item.id}
                    style={{ color: item.color }}
                  >
                    {item.title}
                  </div>
                ))}
              </div>
            ) : null}
            {!isLeaveCommentBtn && (
              <div className='product-card-new__bottom'>
                {variants.length > 1 && !isStatic ? (
                  <WeightTabs
                    isDesktop={isDesktop}
                    onChangeWeightTabHandler={onChangeWeightTabHandler}
                    variants={variants}
                    variantInfoId={id}
                  />
                ) : (
                  <div className='product-card-new__measure'>
                    {priceBlockWeightPrice
                      ? symbolPriceField.slice(5)
                      : `за ${measures.prepare({
                          measure:
                            measureItem == null ? '' : measureItem?.symbol,
                          weight: weight_of_goods
                        })}`}
                  </div>
                )}
                <div
                  className={clsx(
                    `product-card-new__price-btn`,
                    isShowLongBuyBtn && ' product-card-new__price-btn--d-block'
                  )}
                >
                  {(isStatic ||
                    (isMenu && !isBarMenuCurrentPath) ||
                    (available &&
                      (isDesktop ||
                        isShowLongBuyBtn ||
                        productCount === 0 ||
                        productCount === undefined))) && (
                    <div
                      className={twcls(
                        'product-card-new__price-wrapper',
                        productCount > 0 &&
                          'product-card-new__price-wrapper--centered',
                        isShowLongBuyBtn &&
                          'product-card-new__price-wrapper--reversed'
                      )}
                    >
                      <>
                        {specialPriceData ? (
                          <div className='product-card-new__special'>
                            {specialPriceData.price1Kg ||
                              specialPriceData.price}{' '}
                            ₽ от {specialPriceData.amount} шт
                          </div>
                        ) : oldPriceField.wholePart > 0 ? (
                          <div className='product-card-new__old-price'>
                            {oldPriceField.wholePart}
                            {oldPriceField.floatPart && (
                              <span className='product-card-new__old-price__decimal'>
                                ,{oldPriceField.floatPart}
                              </span>
                            )}
                            <span className='product-card-new__old-price-ruble'>
                              <span>&nbsp;₽</span>
                            </span>
                          </div>
                        ) : null}
                        <div
                          className={twcls(
                            'product-card-new__price',
                            oldPriceField.wholePart > 0 &&
                              !specialPriceData &&
                              'product-card-new__price--red',
                            isBigMobileCard && BIG_MOBILE_PRICE__CLS
                          )}
                        >
                          {currentPriceField.wholePart}
                          {currentPriceField.floatPart && (
                            <span
                              className={twcls(
                                'product-card-new__price__decimal',
                                isBigMobileCard && BIG_MOBILE_PRICE_PART__CLS
                              )}
                            >
                              ,{currentPriceField.floatPart}
                            </span>
                          )}
                          <span
                            className={twcls(
                              'product-card-new__price-ruble',
                              isBigMobileCard && BIG_MOBILE_PRICE_PART__CLS
                            )}
                          >
                            <span>&nbsp;₽</span>
                            {priceMeasure}
                          </span>
                        </div>
                      </>
                    </div>
                  )}
                  {!isStatic &&
                    getProductCanBeBought({
                      isProductMenu: !!isMenu,
                      isNotForSale: is_not_for_sale === 'Y',
                      pathname: location.pathname
                    }) && <ProductBuyBtn {...buyBtnProps} />}
                </div>
              </div>
            )}
            {isLeaveCommentBtn && (
              <div
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  leaveReviewHandler();
                }}
                className='product-card-new__review-btn'
              >
                Оставить отзыв
              </div>
            )}
          </div>
        </>
      );
      if (isStatic) {
        return (
          <div
            className={twcls(
              'banners-link product-card-new !cursor-default',
              isBigMobileCard && BIG_MOBILE_CARD__CLS
            )}
          >
            {cardContent}
          </div>
        );
      }
      return (
        <BannersLink
          ref={ref}
          openInNewPage={openInNewPage}
          className={twcls(
            'product-card-new',
            isBigMobileCard && BIG_MOBILE_CARD__CLS
          )}
          link={link}
          state={productInfo}
        >
          {cardContent}
        </BannersLink>
      );
    }
  )
);

export default ProductCardNew;
